import React from 'react';

import type { IntlShape } from 'react-intl';

import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { InlineCommentToolbarViewedAEP } from '@atlaskit/editor-common/analytics/types/inline-comment-events';
import { VIEW_METHOD } from '@atlaskit/editor-common/analytics/types/inline-comment-events';
import { isOfflineMode } from '@atlaskit/editor-common/connectivity/isOfflineMode';
import { ToolTipContent } from '@atlaskit/editor-common/keymaps';
import { commentMessages as messages } from '@atlaskit/editor-common/media/comments';
import { annotationMessages } from '@atlaskit/editor-common/messages/annotation';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type { FloatingToolbarButton } from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import CommentIcon from '@atlaskit/icon/core/comment';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { MediaNextEditorPluginType } from '../../mediaPluginType';
import type { MediaPluginOptions } from '../../types';
import { getSelectedMediaSingle } from './utils';

export const commentButton = (
	intl: IntlShape,
	state: EditorState,
	api: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined,
	onCommentButtonMount?: () => void,
	createCommentExperience?: MediaPluginOptions['createCommentExperience'],
): FloatingToolbarButton<Command> => {
	const selectMediaNode = getSelectedMediaSingle(state)?.node.firstChild;
	let hasActiveComments = false;
	const annotations = api?.annotation?.sharedState.currentState()?.annotations;

	if (selectMediaNode && annotations) {
		hasActiveComments = selectMediaNode.marks.some(
			(mark) => mark.type.name === 'annotation' && !annotations[mark.attrs.id],
		);
	}
	const title = intl.formatMessage(
		hasActiveComments ? messages.viewCommentsOnMedia : messages.addCommentOnMedia,
	);

	const buttonLabel = intl.formatMessage(annotationMessages.createComment);

	const onClickHandler = (state: EditorState, dispatch?: CommandDispatch) => {
		if (api?.annotation && selectMediaNode) {
			const { showCommentForBlockNode, setInlineCommentDraftState } = api.annotation.actions;
			const isOpeningMediaCommentFromToolbar = fg('confluence_frontend_media_scroll_fix')
				? true
				: false;

			if (
				!showCommentForBlockNode(
					selectMediaNode,
					VIEW_METHOD.COMMENT_BUTTON,
					isOpeningMediaCommentFromToolbar,
				)(state, dispatch)
			) {
				createCommentExperience?.start({
					attributes: {
						pageClass: 'editor',
						commentType: 'block',
						blockType: 'media',
						entryPoint: 'highlightActionsSimple',
					},
				});
				createCommentExperience?.initExperience.start();

				setInlineCommentDraftState(
					true,
					// TODO: ED-26962 - might need to update to reflect it's from media floating toolbar
					INPUT_METHOD.FLOATING_TB,
					'block',
					selectMediaNode.attrs?.id,
					isOpeningMediaCommentFromToolbar,
				)(state, dispatch);
			}
		}
		return true;
	};

	return {
		type: 'button',
		testId: 'add-comment-media-button',
		icon: CommentIcon,
		title: editorExperiment('platform_editor_controls', 'control') ? title : buttonLabel,
		showTitle: editorExperiment('platform_editor_controls', 'control') ? undefined : true,
		onClick: onClickHandler,
		tooltipContent: <ToolTipContent description={title} />,
		supportsViewMode: true,
		disabled: isOfflineMode(api?.connectivity?.sharedState?.currentState()?.mode),
		onMount: () => {
			onCommentButtonMount && onCommentButtonMount();
			if (fg('cc_maui_annotations_on_extensions')) {
				const payload: InlineCommentToolbarViewedAEP = {
					eventType: EVENT_TYPE.SCREEN,
					name: 'inlineCommentToolbar',
					action: ACTION.VIEWED,
					actionSubject: ACTION_SUBJECT.INLINE_COMMENT_TOOLBAR,
					attributes: {
						commentTargetType: 'media',
						hasExistingComments: hasActiveComments,
						isCommentButtonEnabled: !isOfflineMode(
							api?.connectivity?.sharedState?.currentState()?.mode,
						),
					},
				};
				api?.analytics?.actions.fireAnalyticsEvent(payload);
			}
		},
	};
};
